import { useEffect, useState } from 'react'
import styled from '@emotion/styled'
import { ViewList, Share, Grain } from '@mui/icons-material'
import { Typography } from '@mui/material'
import { motion } from 'framer-motion'
import { Users as HeaderIcon } from 'lucide-react'
import { useSelector, shallowEqual } from 'react-redux'
import { Paper } from '@components'
import { AdminHeader } from '@components/layout/admin-header'
import { colors } from '@global/constants'
import { api } from 'api'
import { AdminSearch } from './admin-search'
import { GlobalReport } from './global-report'
import { SubmissionHistory } from './submission-history'
import { TemplateReport } from './template-report'
import { UsageReport } from './usage-report'
import { UserReport } from './user-report'
import { t } from '@lingui/core/macro'

const BaseStyle = styled(motion.div)`
  width: 100%;
  display: flex;
  flex-direction: column;
  max-height: 100%;
  height: 100%;
  overflow-y: scroll;
`

const Scroll = styled.div`
  height: 100%;
  overflow-y: scroll;
`

const PaperPadding = styled(Paper)`
  border-radius: 0;
`

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 32px 44px 28px 44px;
`

const IconContainer = styled.div`
  display: flex;
  max-width: 300px;
  margin: 10px;
`

export const AdminReports = () => {
  const [summary, setSummary] = useState({})

  const hasGlobalReport = useSelector(state => state.Auth.features['global-report'], shallowEqual)

  useEffect(() => {
    api.get('/api/v1/account/reports/summary').then(({ data }) => {
      setSummary(data)
    })
  }, [])

  const animation = {
    initial: { opacity: 0 },
    animate: { opacity: 1, transition: { duration: 0.5 } },
    exit: { opacity: 0, transition: { duration: 0.5 } },
    style: { overflow: 'hidden' },
  }

  return (
    <BaseStyle
      initial={animation.initial}
      animate={animation.animate}
      exit={animation.exit}
      style={animation.style}
    >
      <AdminHeader>
        <AdminHeader.PageIcon>
          <HeaderIcon color="hsla(194, 47%, 20%, 1)" size={28} />
        </AdminHeader.PageIcon>
        <div className="ml-4">
          <AdminHeader.Title>{t`Reports`}</AdminHeader.Title>
          <AdminHeader.Subtitle>
            {t`Reports only reflects activity from successful submissions. Failed, or pending submissions are not included in these reports.`}
          </AdminHeader.Subtitle>
        </div>
      </AdminHeader>
      <Scroll>
        <HeaderContainer>
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0, transition: { duration: 0.25, delay: 1 * 0.1 + 0.1 } }}
            key="summary"
          >
            <div style={{ display: 'flex' }}>
              <IconContainer>
                <Grain style={{ color: colors.orange.main, fontSize: '35px' }} />
                <div>
                  <Typography variant="h2" style={{ fontSize: '26px', letterSpacing: 1 }}>
                    {summary.activeUsers ? Intl.NumberFormat().format(summary.activeUsers) : 0}
                  </Typography>
                  <Typography variant="body2">{t`Users`}</Typography>
                </div>
              </IconContainer>
              <IconContainer>
                <Share style={{ color: colors.purple.main, fontSize: '35px' }} />
                <div>
                  <Typography variant="h2" style={{ fontSize: '26px', letterSpacing: 1 }}>
                    {summary.numCodesSubmitted
                      ? Intl.NumberFormat().format(summary.numCodesSubmitted)
                      : 0}
                  </Typography>
                  <Typography variant="body2">{t`Rows`}</Typography>
                </div>
              </IconContainer>
              <IconContainer>
                <ViewList style={{ color: colors.green.main, fontSize: '35px' }} />
                <div>
                  <Typography variant="h2" style={{ fontSize: '26px', letterSpacing: 1 }}>
                    {summary.submissions ? Intl.NumberFormat().format(summary.submissions) : 0}
                  </Typography>
                  <Typography variant="body2">{t`Submissions`}</Typography>
                </div>
              </IconContainer>
            </div>
          </motion.div>
        </HeaderContainer>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0, transition: { duration: 0.25, delay: 2 * 0.1 + 0.1 } }}
          key="history"
          style={{ borderTop: '1px solid #e8e8e8' }}
        >
          <PaperPadding>
            <SubmissionHistory />
          </PaperPadding>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0, transition: { duration: 0.25, delay: 3 * 0.1 + 0.1 } }}
          key="user"
          style={{ borderTop: '1px solid #e8e8e8' }}
        >
          <PaperPadding>
            <UserReport />
          </PaperPadding>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0, transition: { duration: 0.25, delay: 4 * 0.1 + 0.1 } }}
          key="usage"
          style={{ borderTop: '1px solid #e8e8e8' }}
        >
          <PaperPadding>
            <UsageReport />
          </PaperPadding>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0, transition: { duration: 0.25, delay: 5 * 0.1 + 0.1 } }}
          key="template"
          style={{ borderTop: '1px solid #e8e8e8' }}
        >
          <PaperPadding>
            <TemplateReport />
          </PaperPadding>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0, transition: { duration: 0.25, delay: 6 * 0.1 + 0.1 } }}
          key="search-report"
          style={{ borderTop: '1px solid #e8e8e8' }}
        >
          <PaperPadding>
            <AdminSearch />
          </PaperPadding>
        </motion.div>
        {hasGlobalReport && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0, transition: { duration: 0.25, delay: 7 * 0.1 + 0.1 } }}
            key="global"
            style={{ borderTop: '1px solid #e8e8e8' }}
          >
            <PaperPadding>
              <GlobalReport />
            </PaperPadding>
          </motion.div>
        )}
      </Scroll>
    </BaseStyle>
  )
}
